import { useCallback, useEffect, useState } from "react";
import { IStandardsModalTab } from "../../types/IStandardsModalTab";
import { STANDARDS_SET_MODE } from "../../types/IStandardsSetMode";
import { useStandardsSearchContext } from "../StandardsSearchProvider";
import {
  useStandardsModalTabsNavigation,
  useStandardsSet,
  useStandardsSetFilter,
} from "./hooks";
import { StandardsModalContext } from "./StandardsModalProvider.context";
import {
  IStandardsModalContext,
  IStandardsModalProvider,
} from "./StandardsModalProvider.types";

const StandardsModalProvider = ({ children }: IStandardsModalProvider) => {
  const { allStandardsModalResults } = useStandardsSearchContext();
  const [modalTabs, setModalTabs] = useState<IStandardsModalTab[]>([]);

  const {
    newStandardsSet,
    setNewStandardsSet,
    createdStandardsSets,
    addNewStandardsSet,
    editStandardsSet,
    deleteStandardsSet,
    standardsSetTitleError,
    setStandardsSetTitleError,
    isEditingSet,
    setIsEditingSet,
    standardsSetMode,
    setStandardsSetMode,
    confirmModalData,
    setConfirmModalData,
    isConfirmActionModalOpen,
    setIsConfirmActionModalOpen,
    openConfirmModal,
    closeConfirmModal,
    draftStandardsSetId,
    setDraftStandardsSetId,
    handleCreateDraftStandardsSet,
    operationErrorMessage,
    resetOperationErrorMessage,
    isPublishingStandardsSet,
    setIsPublishingStandardsSet,
    isUpdatingStandardsSet,
    setIsUpdatingStandardsSet,
    isDeletingStandardsSet,
    setIsDeletingStandardsSet,
  } = useStandardsSet();

  const {
    currentStandards,
    setCurrentStandards,
    filteredStandards,
    setFilteredStandards,
    selectedSubjectFilter,
    setSelectedSubjectFilter,
    selectedDateFilter,
    setSelectedDateFilter,
    availableDates,
    availableSubjects,
  } = useStandardsSetFilter(allStandardsModalResults);

  const {
    switchEditDataOnTabChange,
    allowModalTabAutoChanges,
    setAllowModalTabAutoChanges,
  } = useStandardsModalTabsNavigation(
    createdStandardsSets,
    setConfirmModalData,
    modalTabs,
  );

  const handleApplyConfirmModal = useCallback(() => {
    const { setId } = confirmModalData;
    if (standardsSetMode === STANDARDS_SET_MODE.DELETE && setId) {
      deleteStandardsSet(setId);
    } else if (operationErrorMessage !== "") {
      resetOperationErrorMessage();
      setIsPublishingStandardsSet(false);
      setIsUpdatingStandardsSet(false);
      setIsDeletingStandardsSet(false);
      if (newStandardsSet.id === "") {
        handleCreateDraftStandardsSet();
      }
    }
    setIsConfirmActionModalOpen(false);
  }, [
    createdStandardsSets,
    standardsSetMode,
    confirmModalData,
    operationErrorMessage,
  ]);

  useEffect(() => {
    setCurrentStandards(allStandardsModalResults);
  }, [allStandardsModalResults]);

  useEffect(() => {
    if (operationErrorMessage !== "" && !isConfirmActionModalOpen) {
      setIsConfirmActionModalOpen(true);
    }
  }, [operationErrorMessage, isConfirmActionModalOpen]);

  const contextReturn: IStandardsModalContext = {
    getters: {
      currentStandards,
      newStandardsSet,
      createdStandardsSets,
      selectedSubjectFilter,
      selectedDateFilter,
      filteredStandards,
      availableDates,
      availableSubjects,
      isConfirmActionModalOpen,
      standardsSetMode,
      confirmModalData,
      isEditingSet,
      standardsSetTitleError,
      modalTabs,
      draftStandardsSetId,
      operationErrorMessage,
      isPublishingStandardsSet,
      isUpdatingStandardsSet,
      isDeletingStandardsSet,
      allowModalTabAutoChanges,
    },
    handlers: {
      setCurrentStandards,
      setNewStandardsSet,
      addNewStandardsSet,
      editStandardsSet,
      deleteStandardsSet,
      setSelectedSubjectFilter,
      setSelectedDateFilter,
      setFilteredStandards,
      openConfirmModal,
      closeConfirmModal,
      setStandardsSetMode,
      setConfirmModalData,
      handleApplyConfirmModal,
      setIsEditingSet,
      setStandardsSetTitleError,
      switchEditDataOnTabChange,
      setModalTabs,
      setDraftStandardsSetId,
      handleCreateDraftStandardsSet,
      setAllowModalTabAutoChanges,
    },
  };

  return (
    <StandardsModalContext.Provider value={contextReturn}>
      {children}
    </StandardsModalContext.Provider>
  );
};

StandardsModalProvider.displayName = "StandardsModalProvider";
export { StandardsModalProvider };
